/**
 * gs-product-bundles.js
 * GripSocks volume-tier picker for the PDP.
 * Handles tier selection, keyboard nav, and re-prices on variant change.
 * Ported from SeatShield ss-product-bundles.js (2026-06-07)
 */
(function () {
  'use strict';

  const TIER_WRAPPER_ATTR = 'data-gs-tiers';
  const TIER_ATTR = 'data-gs-tier';
  const TIER_INPUT_ATTR = 'data-gs-tier-input';
  const BUNDLES_ATTR = 'data-gs-product-bundles';

  /* ------------------------------------------------------------------ */
  /* Tier selection                                                        */
  /* ------------------------------------------------------------------ */
  function selectTier(tierEl) {
    const wrapper = tierEl.closest('[' + TIER_WRAPPER_ATTR + ']');
    if (!wrapper) return;

    wrapper.querySelectorAll('[' + TIER_ATTR + ']').forEach(function (t) {
      t.classList.remove('gs-product-bundles__tier--selected');
      t.setAttribute('aria-checked', 'false');
    });

    tierEl.classList.add('gs-product-bundles__tier--selected');
    tierEl.setAttribute('aria-checked', 'true');

    const input = tierEl.querySelector('[' + TIER_INPUT_ATTR + ']');
    if (input) input.checked = true;

    updateAddToCartQty(tierEl);
  }

  function updateAddToCartQty(tierEl) {
    const qty = parseInt(tierEl.dataset.tierQty, 10);
    if (isNaN(qty)) return;

    // Update the quantity input in the add-to-cart form on the page.
    // Impulse uses data-quantity-input or a plain input[name="quantity"].
    const form = document.querySelector('.product-single__form, form[action*="/cart/add"]');
    if (!form) return;

    // Find the form's quantity input. Impulse's product-form has none of its own
    // (the stepper is a separate quantity_selector block), so if it's absent we
    // inject a hidden one — otherwise the add submits with no quantity and Shopify
    // silently defaults the line to 1 regardless of the selected tier.
    let qtyInput = form.querySelector('[data-quantity-input], input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      qtyInput.setAttribute('data-gs-injected-qty', '');
      form.appendChild(qtyInput);
    }
    qtyInput.value = qty;
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------ */
  /* Keyboard navigation (arrow keys within radiogroup)                   */
  /* ------------------------------------------------------------------ */
  function handleKeydown(e, tierEl) {
    const wrapper = tierEl.closest('[' + TIER_WRAPPER_ATTR + ']');
    if (!wrapper) return;
    const tiers = Array.from(wrapper.querySelectorAll('[' + TIER_ATTR + ']'));
    const idx = tiers.indexOf(tierEl);

    let nextIdx = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      nextIdx = (idx + 1) % tiers.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      nextIdx = (idx - 1 + tiers.length) % tiers.length;
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectTier(tierEl);
      return;
    }

    if (nextIdx !== idx) {
      selectTier(tiers[nextIdx]);
      tiers[nextIdx].focus();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Re-price all tiers when variant changes                              */
  /* ------------------------------------------------------------------ */
  function repriceTiers(newPrice) {
    document.querySelectorAll('[' + BUNDLES_ATTR + ']').forEach(function (bundleEl) {
      bundleEl.querySelectorAll('[' + TIER_ATTR + ']').forEach(function (tierEl) {
        const pct = parseFloat(tierEl.dataset.tierPct || '0');
        if (!pct) return;

        const qty = parseInt(tierEl.dataset.tierQty, 10) || 1;

        // Per-unit discounted price, mirroring Shopify's discount engine:
        // the per-UNIT discount is FLOORED to the cent (sale price rounds UP,
        // never down). Precision guard: round to 6 decimals first so float
        // artifacts like 459.89999999 don't floor to 458.
        const discountRaw = newPrice * pct / 100;
        const discountCents = Math.floor(Math.round(discountRaw * 1e6) / 1e6);
        const discountedUnit = newPrice - discountCents;

        const priceEl = tierEl.querySelector('[data-display-price]');
        if (priceEl) {
          const suffixEl = priceEl.querySelector('[data-price-suffix]');
          const suffixHtml = suffixEl ? suffixEl.outerHTML : '';
          priceEl.innerHTML = formatMoney(discountedUnit) + suffixHtml;
        }

        // Recompute totals + savings rows (present when "Show tier totals
        // + savings" is enabled in the customizer)
        const regTotalCents = newPrice * qty;
        const saleTotalCents = discountedUnit * qty;
        const savingsCents = regTotalCents - saleTotalCents;

        const regTotalEl = tierEl.querySelector('[data-reg-total]');
        if (regTotalEl) regTotalEl.textContent = formatMoney(regTotalCents);

        const saleTotalEl = tierEl.querySelector('[data-sale-total]');
        if (saleTotalEl) saleTotalEl.textContent = formatMoney(saleTotalCents);

        const savingEl = tierEl.querySelector('[data-tier-saving]');
        if (savingEl) {
          if (savingsCents > 0) {
            const template = savingEl.dataset.savingTemplate || 'save __AMT__';
            savingEl.textContent = template.replace('__AMT__', formatMoney(savingsCents));
            savingEl.hidden = false;
          } else {
            savingEl.hidden = true;
          }
        }

        // Keep data attr in sync for any downstream logic
        tierEl.dataset.tierUnitPrice = discountedUnit;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Money formatting (mirrors Shopify money_with_currency approximation) */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents) {
    const symbol = window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol
      ? window.Shopify.currency.symbol
      : '$';
    return symbol + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ------------------------------------------------------------------ */
  /* Event wiring (delegated on document)                                 */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    const tierEl = e.target.closest('[' + TIER_ATTR + ']');
    if (!tierEl) return;
    selectTier(tierEl);
  });

  document.addEventListener('keydown', function (e) {
    const tierEl = e.target.closest('[' + TIER_ATTR + ']');
    if (!tierEl) return;
    handleKeydown(e, tierEl);
  });

  // Shopify Impulse emits this custom event when variant changes
  document.addEventListener('variant:change', function (e) {
    if (!e.detail || !e.detail.variant) return;
    repriceTiers(e.detail.variant.price);
  });

  // Also listen to Impulse's variantChange event name (older builds)
  document.addEventListener('variantChange', function (e) {
    if (!e.detail || !e.detail.variant) return;
    repriceTiers(e.detail.variant.price);
  });

  /* ------------------------------------------------------------------ */
  /* Make tier labels focusable (for keyboard nav to work)                */
  /* ------------------------------------------------------------------ */
  function makeTiersFocusable() {
    document.querySelectorAll('[' + TIER_ATTR + ']').forEach(function (tierEl) {
      if (!tierEl.hasAttribute('tabindex')) {
        tierEl.setAttribute('tabindex', '0');
      }
    });
  }

  // Run once DOM is ready (script is deferred so DOM is always ready)
  makeTiersFocusable();

  /* ------------------------------------------------------------------ */
  /* Sync the add-to-cart quantity to the server-pre-selected tier on    */
  /* load, so "Add to cart" without clicking a tier uses the default     */
  /* tier's quantity (e.g. tier 2 = qty 3), not Impulse's default of 1.  */
  /* ------------------------------------------------------------------ */
  function syncInitialQty() {
    document.querySelectorAll('[' + BUNDLES_ATTR + ']').forEach(function (bundleEl) {
      const selected = bundleEl.querySelector('.gs-product-bundles__tier--selected[' + TIER_ATTR + ']')
        || bundleEl.querySelector('[' + TIER_ATTR + ']');
      if (selected) updateAddToCartQty(selected);
    });
  }
  syncInitialQty();

})();
